import {
    CarOutlined,
    DeleteOutlined,
    DownOutlined,
    DownloadOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, Dropdown, Grid, Menu, Modal, Space, Tabs, message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import { type ModelConfiguratorSpecsFragment } from '../../../api/fragments/ModelConfiguratorSpecs';
import { type VariantConfiguratorSpecsFragment } from '../../../api/fragments/VariantConfiguratorSpecs';
import {
    DeleteModelConfiguratorDocument,
    type DeleteModelConfiguratorMutation,
    type DeleteModelConfiguratorMutationVariables,
} from '../../../api/mutations/deleteModelConfigurator';
import {
    UpdateModelConfiguratorDocument,
    type UpdateModelConfiguratorMutation,
    type UpdateModelConfiguratorMutationVariables,
} from '../../../api/mutations/updateModelConfigurator';
import { useGetModelConfiguratorLazyQuery } from '../../../api/queries/getModelConfigurator';
import {
    ApplicationStage,
    ContentRefinementSourceAction,
    ContentRefinementSourceKind,
    ModuleType,
} from '../../../api/types';
import { useAccountContext } from '../../../components/contexts/AccountContextManager';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import { useMultipleDealerIds } from '../../../components/contexts/DealerContextManager';
import { GenericStateOnPageContextProvider } from '../../../components/contexts/GenericStateOnPageContextManager';
import {
    type ExportModalInput,
    useExportFormatModal,
} from '../../../components/fields/DownloadModal/ExportFormatModal';
import { usePasswordModal } from '../../../components/fields/DownloadModal/PasswordModal';
import Form from '../../../components/fields/Form';
import FormAutoTouch from '../../../components/FormAutoTouch';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import {
    ExportFormat,
    exportConfiguratorApplication,
    exportConfiguratorVariantPrices,
} from '../../../utilities/export';
import getApolloErrors from '../../../utilities/getApolloErrors';
import hasPermissions from '../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import useHandleError from '../../../utilities/useHandleError';
import AddVariantConfiguratorModal from './AddVariantConfiguratorModal';
import ApplicationTabList from './ApplicationTabList';
import CopyVariantConfiguratorModal from './CopyVariantConfiguratorModal';
import { useImportVariantPricesModal } from './ImportVariantPricesModal';
import InventoryConfiguratorList from './InventoryConfiguratorList';
import ModelConfiguratorForm, {
    FORM_NAME,
    LIST_URL,
    type ModelConfiguratorFormValues,
    useHandleModelConfiguratorAssets,
    useModelConfiguratorFormValidator,
} from './ModelConfiguratorForm';
import { useModelConfigurator } from './ModelConfiguratorProvider';
import VariantConfiguratorList from './VariantConfiguratorList';

export enum HeaderTabs {
    Details = 'details',
    Variants = 'variants',
    Applications = 'applications',
    Reservations = 'reservations',
    Leads = 'leads',
    Inventory = 'inventory',
}

export type ModelConfiguratorDetailsInnerPageProps = {
    modelConfigurator: ModelConfiguratorSpecsFragment;
};

const ModelConfiguratorDetailsInnerPage = () => {
    const { t } = useTranslation(['modelConfiguratorDetails', 'variantConfiguratorDetails']);

    const { modelConfigurator } = useModelConfigurator();
    const modelConfiguratorId = modelConfigurator.id;
    const company = useCompany(true);
    const location = useLocation();
    const [activeTab, setActiveTab] = useState<HeaderTabs>(location.state?.tabs || HeaderTabs.Details);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const importVariantPricesModal = useImportVariantPricesModal();
    const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);

    const [selectedFinanceApplications, setSelectedFinanceApplications] = useState<string[]>([]);
    const [selectedReservations, setSelectedReservations] = useState<string[]>([]);
    const [selectedLeads, setSelectedLeads] = useState<string[]>([]);
    const screens = Grid.useBreakpoint();
    const { dealerIds } = useMultipleDealerIds();

    const apolloClient = useApolloClient();
    const { token } = useAccountContext();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: modelConfigurator.versioning,
        moduleId: modelConfigurator.moduleId,
    });

    const handleModelConfiguratorAssets = useHandleModelConfiguratorAssets();

    const passwordModal = usePasswordModal();

    const navigate = useNavigate();

    const validate = useModelConfiguratorFormValidator();

    const [hasUpdatePermission, hasDeletePermission] = useMemo(
        () => [
            hasPermissions(modelConfigurator.permissions, [permissionKind.updateConfigurator]),
            hasPermissions(modelConfigurator.permissions, [permissionKind.deleteConfigurator]),
        ],
        [modelConfigurator.permissions]
    );

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Configurator,
                action: ContentRefinementSourceAction.Update,
                identifier: modelConfiguratorId,
            },
        }),
        [hasUpdatePermission, modelConfiguratorId]
    );

    const [, { refetch }] = useGetModelConfiguratorLazyQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            id: modelConfiguratorId,
        },
    });

    const onSubmit = useHandleError<ModelConfiguratorFormValues>(
        async values => {
            const { banner, mobileBanner, descriptionImages, ...settings } = values;

            message.loading({
                content: t('modelConfiguratorDetails:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const { data } = await apolloClient
                .mutate<UpdateModelConfiguratorMutation, UpdateModelConfiguratorMutationVariables>({
                    mutation: UpdateModelConfiguratorDocument,
                    variables: {
                        id: modelConfiguratorId,
                        settings,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            await handleModelConfiguratorAssets(data.modelConfigurator.id, values, modelConfigurator);

            refetch();

            // inform about success
            message.success({
                content: t('modelConfiguratorDetails:messages.updateSuccessful'),
                key: 'primary',
            });

            // Navigate to detail
            navigate(`/admin/configurators/${data.modelConfigurator.id}`, {
                state: { tabs: activeTab },
            });
        },
        [
            activeTab,
            apolloClient,
            handleModelConfiguratorAssets,
            modelConfigurator,
            modelConfiguratorId,
            navigate,
            refetch,
            t,
        ]
    );

    const deleteModelConfigurator = useCallback(() => {
        Modal.confirm({
            title: t('modelConfiguratorDetails:modals.delete.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('modelConfiguratorDetails:modals.delete.content'),
            okText: t('modelConfiguratorDetails:modals.delete.okText'),
            okType: 'danger',
            cancelText: t('modelConfiguratorDetails:modals.delete.cancelText'),
            async onOk() {
                try {
                    // loading message
                    message.loading({
                        content: t('modelConfiguratorDetails:messages.deleteSubmitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    // delete with the API
                    const { data: deletionData } = await apolloClient
                        .mutate<DeleteModelConfiguratorMutation, DeleteModelConfiguratorMutationVariables>({
                            mutation: DeleteModelConfiguratorDocument,
                            variables: { id: modelConfiguratorId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    if (deletionData?.isDeleted) {
                        // show success
                        message.success({
                            content: t('modelConfiguratorDetails:messages.deleteSuccessful'),
                            key: 'primary',
                        });
                    } else {
                        throw new Error(t('modelConfiguratorDetails:messages.deleteFailed'));
                    }

                    // then go back to list
                    navigate(LIST_URL);
                } catch (error) {
                    const apolloErrors = getApolloErrors(error);

                    if (apolloErrors?.$root) {
                        message.error(apolloErrors?.$root);
                    } else if (apolloErrors?.id) {
                        message.error(apolloErrors?.id);
                    }
                }
            },
        });
    }, [apolloClient, modelConfiguratorId, navigate, t]);

    const onVariantCreationSuccess = useCallback(
        (variantConfigurator: VariantConfiguratorSpecsFragment) => {
            setIsModalVisible(false);

            message.success({
                content: t('variantConfiguratorDetails:messages.variant.createSuccessful'),
                key: 'primary',
            });

            navigate(`/admin/configurators/${modelConfigurator.id}/${variantConfigurator.id}`, {
                state: { tabs: activeTab, configuratorId: modelConfigurator },
            });
        },
        [activeTab, modelConfigurator, navigate, t]
    );

    const onVariantCopySuccess = useCallback(
        (variantConfigurator: VariantConfiguratorSpecsFragment) => {
            setIsCopyModalVisible(false);

            message.success({
                content: t('variantConfiguratorDetails:messages.variant.copySuccessful'),
                key: 'primary',
            });

            navigate(`/admin/configurators/${modelConfigurator.id}/${variantConfigurator.id}`, {
                state: { tabs: activeTab, configuratorId: modelConfigurator },
            });
        },
        [activeTab, modelConfigurator, navigate, t]
    );

    const priceDisclaimer = useMemo(
        () =>
            modelConfigurator.module.__typename === ModuleType.ConfiguratorModule
                ? modelConfigurator.module.priceDisclaimer
                : null,
        [modelConfigurator.module]
    );

    const initialValues = useMemo<ModelConfiguratorFormValues>(
        () => ({
            ...pick(
                [
                    'isActive',
                    'externalUrl',
                    'bannerText',
                    'bannerTextPosition',
                    'pageTitle',
                    'pageSubtitle',
                    'pageDescription',
                    'configuratorTitle',
                    'banner',
                    'urlIdentifier',
                    'mobileBanner',
                    'descriptionImages',
                ],
                modelConfigurator
            ),
            priceDisclaimer,
        }),
        [modelConfigurator, priceDisclaimer]
    );

    const extraMenuItems = useMemo(() => {
        const defaultExtraMenuItems =
            activeTab === HeaderTabs.Variants
                ? [
                      hasUpdatePermission && {
                          label: t('modelConfiguratorDetails:actions.addVariant'),
                          key: 'addVariantConfigurator',
                          icon: <CarOutlined />,
                          onClick: () => setIsModalVisible(true),
                          danger: false,
                      },
                      hasUpdatePermission && {
                          label: t('modelConfiguratorDetails:actions.copyVariant'),
                          key: 'copyVariantConfigurator',
                          icon: <CarOutlined />,
                          onClick: () => setIsCopyModalVisible(true),
                          danger: false,
                      },
                      hasUpdatePermission && {
                          label: t('modelConfiguratorDetails:actions.importVariantPrices'),
                          key: 'importVariantPrices',
                          icon: <CarOutlined />,
                          onClick: importVariantPricesModal.open,
                          danger: false,
                      },
                      hasDeletePermission && {
                          label: t('modelConfiguratorDetails:actions.deleteModel'),
                          key: 'delete',
                          icon: <DeleteOutlined />,
                          onClick: () => deleteModelConfigurator(),
                          danger: true,
                      },
                  ].filter(Boolean)
                : [
                      hasDeletePermission && {
                          label: t('modelConfiguratorDetails:actions.deleteModel'),
                          key: 'delete',
                          icon: <DeleteOutlined />,
                          onClick: () => deleteModelConfigurator(),
                          danger: true,
                      },
                  ].filter(Boolean);

        return defaultExtraMenuItems;
    }, [
        activeTab,
        hasUpdatePermission,
        t,
        importVariantPricesModal.open,
        hasDeletePermission,
        deleteModelConfigurator,
    ]);

    const exportFormatModal = useExportFormatModal({ format: ExportFormat.CapFormat });

    const downloadHandler = useCallback(
        async ({ format }: ExportModalInput) => {
            message.loading({
                content: t(`modelConfiguratorDetails:messages.loadingExportConfiguratorLeads`),
                key: 'loading',
            });

            const getExportInfoByTab = (tab: HeaderTabs) => {
                switch (tab) {
                    case HeaderTabs.Applications:
                        return { stage: ApplicationStage.Financing, applicationIds: selectedFinanceApplications };

                    case HeaderTabs.Reservations:
                        return { stage: ApplicationStage.Reservation, applicationIds: selectedReservations };

                    case HeaderTabs.Leads:
                        return { stage: ApplicationStage.Lead, applicationIds: selectedLeads };

                    default:
                        throw new Error('Invalid tab for downloading applications');
                }
            };

            const { stage, applicationIds } = getExportInfoByTab(activeTab);

            const password = await exportConfiguratorApplication({
                configuratorId: modelConfiguratorId,
                applicationIds,
                stage,
                dealerIds,
                company: { countryCode: company?.countryCode, displayName: company?.displayName },
                token,
                format,
            });

            message.destroy('loading');
            exportFormatModal.close();
            if (password) {
                passwordModal.open(password);
            }
        },
        [
            t,
            activeTab,
            modelConfiguratorId,
            dealerIds,
            company?.countryCode,
            company?.displayName,
            token,
            exportFormatModal,
            selectedFinanceApplications,
            selectedReservations,
            selectedLeads,
            passwordModal,
        ]
    );

    const onDownloadApplicationClick = useCallback(() => {
        exportFormatModal.open();
    }, [exportFormatModal]);

    const onDownloadVariantPricesClick = useCallback(async () => {
        const isExportSuccess = await exportConfiguratorVariantPrices(
            modelConfiguratorId,
            modelConfigurator.displayName,
            token
        );

        if (isExportSuccess) {
            message.success({
                content: t('modelConfiguratorDetails:messages.successExportVariantPrices'),
                key: 'primary',
            });
        } else {
            message.error({
                content: t('modelConfiguratorDetails:messages.failedExportVariantPrices'),
                key: 'primary',
            });
        }
    }, [modelConfiguratorId, modelConfigurator.displayName, token, t]);

    const extra = (
        <>
            {[HeaderTabs.Applications, HeaderTabs.Leads, HeaderTabs.Reservations].includes(activeTab) && (
                <Button
                    disabled={
                        selectedLeads.length === 0 &&
                        selectedReservations.length === 0 &&
                        selectedFinanceApplications.length === 0
                    }
                    icon={<DownloadOutlined />}
                    onClick={onDownloadApplicationClick}
                >
                    {t('modelConfiguratorDetails:actions.download')}
                </Button>
            )}

            {extraMenuItems.length ? (
                <Dropdown overlay={<Menu items={extraMenuItems} />}>
                    <Button type="primary">
                        <Space>
                            <span>{t('modelConfiguratorDetails:actions.actions')}</span>
                            <DownOutlined />
                        </Space>
                    </Button>
                </Dropdown>
            ) : null}
        </>
    );

    const renderActiveTab = () => {
        switch (activeTab) {
            case HeaderTabs.Details:
                return (
                    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
                        {({ handleSubmit, errors }) => (
                            <Form id={FORM_NAME} name={FORM_NAME} onSubmitCapture={handleSubmit}>
                                <FormAutoTouch />
                                <ModelConfiguratorForm
                                    disabled={!hasUpdatePermission}
                                    offset={offset}
                                    updated={updated}
                                    isEdit
                                />
                            </Form>
                        )}
                    </Formik>
                );

            case HeaderTabs.Variants:
                return <VariantConfiguratorList activeTab={activeTab} modelConfiguratorId={modelConfiguratorId} />;

            case HeaderTabs.Applications:
                return (
                    <ApplicationTabList
                        configuratorId={modelConfiguratorId}
                        setSelectedApplications={setSelectedFinanceApplications}
                        stage={ApplicationStage.Financing}
                    />
                );

            case HeaderTabs.Reservations:
                return (
                    <ApplicationTabList
                        configuratorId={modelConfiguratorId}
                        setSelectedApplications={setSelectedReservations}
                        stage={ApplicationStage.Reservation}
                    />
                );

            case HeaderTabs.Leads:
                return (
                    <ApplicationTabList
                        configuratorId={modelConfiguratorId}
                        setSelectedApplications={setSelectedLeads}
                        stage={ApplicationStage.Lead}
                    />
                );

            case HeaderTabs.Inventory:
                return <InventoryConfiguratorList activeTab={activeTab} modelConfiguratorId={modelConfiguratorId} />;

            default:
                return null;
        }
    };

    const onChangeTab = useCallback(
        (activeKey: HeaderTabs) => {
            setActiveTab(activeKey);
            setSelectedLeads([]);
            setSelectedReservations([]);
            setSelectedFinanceApplications([]);
            navigate(`/admin/configurators/${modelConfigurator.id}`, { state: { tabs: activeKey }, replace: true });
        },
        [modelConfigurator.id, navigate]
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            {passwordModal.render()}
            <ConsolePageWithHeader
                extra={extra}
                footer={[
                    activeTab === HeaderTabs.Details && hasUpdatePermission && (
                        <Button key="submit" form={FORM_NAME} htmlType="submit" type="primary">
                            {t('modelConfiguratorDetails:actions.update')}
                        </Button>
                    ),
                ]}
                header={{
                    // TODO: might blocking tabs navigation when change, to prevent editing loss
                    footer: (
                        <Tabs activeKey={activeTab} onChange={onChangeTab}>
                            <Tabs.TabPane key={HeaderTabs.Details} tab={t('modelConfiguratorDetails:tabs.details')} />
                            <Tabs.TabPane key={HeaderTabs.Variants} tab={t('modelConfiguratorDetails:tabs.variants')} />
                            <Tabs.TabPane
                                key={HeaderTabs.Applications}
                                tab={t('modelConfiguratorDetails:tabs.applications')}
                            />
                            <Tabs.TabPane
                                key={HeaderTabs.Reservations}
                                tab={t('modelConfiguratorDetails:tabs.reservations')}
                            />
                            <Tabs.TabPane key={HeaderTabs.Leads} tab={t('modelConfiguratorDetails:tabs.leads')} />
                            <Tabs.TabPane
                                key={HeaderTabs.Inventory}
                                tab={t('modelConfiguratorDetails:tabs.inventory')}
                            />
                        </Tabs>
                    ),
                }}
                onBack={() => navigate(LIST_URL, { state: { tabs: activeTab } })}
                title={
                    screens.md
                        ? t('modelConfiguratorDetails:title', {
                              name: modelConfigurator.configuratorTitle.defaultValue,
                          })
                        : modelConfigurator.configuratorTitle.defaultValue
                }
            >
                <AddVariantConfiguratorModal
                    isVisible={isModalVisible}
                    modelConfigurator={modelConfigurator}
                    onCancel={() => setIsModalVisible(false)}
                    onSuccess={onVariantCreationSuccess}
                />
                <CopyVariantConfiguratorModal
                    isVisible={isCopyModalVisible}
                    modelConfigurator={modelConfigurator}
                    onCancel={() => setIsCopyModalVisible(false)}
                    onSuccess={onVariantCopySuccess}
                />
                {renderActiveTab()}
                {importVariantPricesModal.render({
                    title: t('modelConfiguratorDetails:modals.importVariantPrices.title'),
                    modelConfiguratorId,
                    onDownloadClick: onDownloadVariantPricesClick,
                })}
                {exportFormatModal.render({
                    title: t('modelConfiguratorDetails:modals.downloadApplication.title'),
                    description: t('modelConfiguratorDetails:modals.downloadApplication.description'),
                    onDownload: downloadHandler,
                    forAdmin: true,
                })}
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default ModelConfiguratorDetailsInnerPage;
